<template>
  <!-- 仪表盘：统计卡按职员+迭代过滤，项目状态分布/最近动态全量 -->
  <div class="dashboard">
    <list-page>
      <search-condition @search="loadOverview" :show-search="true">
        <template #default>
          <employee-select v-model="query.ownerId" placeholder="职员（全部）" @change="loadOverview">
            <template #prefix>负责人</template>
          </employee-select>
          <el-select v-model="query.sprintId" placeholder="全部批次" clearable filterable @change="loadOverview" style="width: 240px">
            <template #prefix>迭代批次</template>
            <el-option v-for="s in sprintOptions" :key="s.id" :label="s.sprintName" :value="s.id"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadOverview">刷新</page-button>
        </template>
      </search-condition>
      <list-table class="dash-body" v-loading="loading">
        <!-- 统计卡：直接作为 list-page 的子元素，不放进表格容器 -->
        <div class="stat-grid">
          <div class="stat-card">
            <div class="label">当前批次进度</div>
            <div class="value">{{ data.iterationProgress ?? 0 }}%</div>
            <div class="sub">{{ data.activeSprintName || '无进行中批次' }}</div>
          </div>
          <div class="stat-card">
            <div class="label">我的待办任务</div>
            <div class="value" style="color:#409eff">{{ data.myTodoCount ?? 0 }}</div>
            <div class="sub">未开始 + 进行中</div>
          </div>
          <div class="stat-card">
            <div class="label">3 天内到期</div>
            <div class="value" :style="{color: dueSoonColor}">{{ data.dueSoonCount ?? 0 }}</div>
            <div class="sub">需关注交付节点</div>
          </div>
          <div class="stat-card">
            <div class="label">延期中</div>
            <div class="value" style="color:#f56c6c">{{ data.overdueCount ?? 0 }}</div>
            <div class="sub">未完成且已超计划完成日</div>
          </div>
          <div class="stat-card">
            <div class="label">延期率</div>
            <div class="value" :style="{color: rateColor(data.delayRate)}">{{ data.delayRate ?? 0 }}%</div>
            <div class="sub">延期完成 ÷ 已完结</div>
          </div>
          <div class="stat-card">
            <div class="label">已排期至</div>
            <div class="value schedule-value">{{ data.scheduleEndDate }}</div>
            <div class="sub" :title="data.scheduleEndTaskName || ''">
              {{ data.scheduleEndTaskName || '暂无未完结排期' }}
            </div>
          </div>
        </div>

        <div class="tip-bar">
          统计口径：「当前批次进度」「我的待办任务」「3 天内到期」「延期中」「延期率」按上方筛选（负责人 + 迭代批次）统计；
          「项目状态分布」「最近动态」为全部项目全量数据，不随筛选变化。
        </div>
        <!-- 我的任务 + 需关注清单 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">我的任务<span class="section-sub">未开始 / 进行中</span></div>
            <yq-table :data="myTasks" height="auto" :empty-text="'当前条件下没有进行中的任务'">
              <yq-table-column label="任务" prop="taskName" min-width="180rem" :show-overflow-tooltip="true">
                <template v-slot="{row}">
                  <span class="link-task" @click="goTask(row)">{{ row.taskName || '未命名任务' }}</span>
                  <span v-if="row.delayState && row.delayState !== '正常'" class="delay-flag">{{ delayText(row) }}</span>
                </template>
              </yq-table-column>
              <yq-table-column label="所属项目" prop="projectCode" width="130rem">
                <template v-slot="{row}">{{ row.projectCode || '—' }}</template>
              </yq-table-column>
              <yq-table-column label="计划完成" prop="planEnd" width="110rem">
                <template v-slot="{row}">{{ dateOnly(row.planEnd) }}</template>
              </yq-table-column>
              <yq-table-column label="进度" prop="progress" width="140rem">
                <template v-slot="{row}">
                  <el-progress :percentage="row.progress || 0" :stroke-width="6" :show-text="false"/>
                  <span class="progress-text">{{ row.progress || 0 }}%</span>
                </template>
              </yq-table-column>
            </yq-table>
          </div>
          <div class="chart-card">
            <div class="section-title">需关注<span class="section-sub">即将到期 / 已延期</span></div>
            <yq-table :data="watchTasks" height="auto" :empty-text="'当前没有需要关注的任务'">
              <yq-table-column label="任务" prop="taskName" min-width="160rem" :show-overflow-tooltip="true">
                <template v-slot="{row}">
                  <span class="link-task" @click="goTask(row)">{{ row.taskName || '未命名任务' }}</span>
                </template>
              </yq-table-column>
              <yq-table-column label="负责人" prop="ownerName" width="90rem">
                <template v-slot="{row}">{{ row.ownerName || '—' }}</template>
              </yq-table-column>
              <yq-table-column label="计划完成" prop="planEnd" width="110rem">
                <template v-slot="{row}">{{ dateOnly(row.planEnd) }}</template>
              </yq-table-column>
              <yq-table-column label="延期" prop="delayState" width="100rem">
                <template v-slot="{row}">
                  <span v-if="row.delayState && row.delayState !== '正常'" class="delay-flag">{{ delayText(row) }}</span>
                  <span v-else class="muted">—</span>
                </template>
              </yq-table-column>
            </yq-table>
          </div>
        </div>
        <!-- 项目状态分布 + 最近动态 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">项目状态分布<span class="section-sub">全部项目</span></div>
            <div v-if="!statusList.length" class="empty-block">暂无项目</div>
            <div v-else class="dist-wrap">
              <div v-for="s in statusList" :key="s.status" class="dist-row">
                <span class="dist-label">{{ s.statusName }}</span>
                <div class="dist-track">
                  <div class="dist-fill" :style="{width: distWidth(s.count) + '%', background: statusColor(s.status)}"/>
                </div>
                <span class="dist-count">{{ s.count }}</span>
              </div>
            </div>
          </div>
          <div class="chart-card">
            <div class="section-title">最近动态<span class="section-sub">全部</span></div>
            <div v-if="!recentLogs.length" class="empty-block">暂无动态</div>
            <div v-else class="log-list">
              <div v-for="log in recentLogs" :key="log.id" class="log-item">
                <div class="log-title">
                  <el-tag size="mini" type="info">{{ log.entityType }}</el-tag>
                  <span class="log-action">{{ log.action }}</span>
                  <span class="log-detail">{{ log.detail }}</span>
                </div>
                <div class="log-time">{{ dateTime(log.createdTime) }} · {{ log.createdName || '—' }}</div>
              </div>
            </div>
          </div>
        </div>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import dashboardApi from "@/api/dz-project/project/dashboardApi";
import sprintApi from "@/api/dz-project/project/sprintApi";
import {ProjectStatusCode} from "../manage/projectStatus";
import YqTable from "@/components/h5/table/YqTable.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "Dashboard",
  components: {ListTable, YqTable, EmployeeSelect},
  data() {
    return {
      query: {ownerId: "", sprintId: ""},
      sprintOptions: [],
      data: {},
      loading: false,
      ProjectStatusCode,
    };
  },
  computed: {
    statusList() {
      return this.data.projectStatusList || [];
    },
    recentLogs() {
      return this.data.recentLogs || [];
    },
    myTasks() {
      return this.data.myTasks || [];
    },
    watchTasks() {
      return this.data.watchTasks || [];
    },
    dueSoonColor() {
      return (this.data.dueSoonCount || 0) > 0 ? "#e6a23c" : "#67c23a";
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  async created() {
    // 默认看当前登录人；迭代默认全部
    let userInfo = null;
    try {
      userInfo = await Util.getUserInfo();
    } catch (e) {
      userInfo = null;
    }
    this.query.ownerId = userInfo && userInfo.id ? userInfo.id : "";
    this.initSprints();
    this.loadOverview();
  },
  methods: {
    async initSprints() {
      const {records} = await sprintApi.queryList({pageNumber: 1, pageSize: 200});
      this.sprintOptions = records || [];
    },
    async loadOverview() {
      this.loading = true;
      try {
        const params = {};
        if (this.query.ownerId) {
          params.ownerId = this.query.ownerId;
        }
        if (this.query.sprintId) {
          params.sprintId = this.query.sprintId;
        }
        const res = await dashboardApi.overview(params);
        this.data = res || {};
      } finally {
        this.loading = false;
      }
    },
    distWidth(count) {
      const max = this.statusList.reduce((m, s) => Math.max(m, Number(s.count) || 0), 0);
      if (!max) {
        return 0;
      }
      return Math.round((Number(count) || 0) * 100 / max);
    },
    statusColor(status) {
      const map = {
        [ProjectStatusCode.REVIEW]: "#909399",
        [ProjectStatusCode.APPROVED]: "#409eff",
        [ProjectStatusCode.DEVELOPING]: "#e6a23c",
        [ProjectStatusCode.SUBMITTED]: "#67c23a",
        [ProjectStatusCode.ACCEPTED]: "#13c2c2",
        [ProjectStatusCode.CLOSED]: "#bdbdbd",
      };
      return map[status] || "#909399";
    },
    rateColor(r) {
      const n = Number(r) || 0;
      return n > 30 ? "#f56c6c" : n > 0 ? "#e6a23c" : "#67c23a";
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    dateTime(v) {
      return v ? String(v).slice(0, 16) : "—";
    },
    delayText(task) {
      if (!task || !task.delayState || task.delayState === "正常") {
        return "";
      }
      if (task.delayState === "延期中") {
        return "延期" + (task.delayDays || 0) + "天";
      }
      return "延期完成";
    },
    goTask(row) {
      if (row && row.id) {
        this.router.push(`/project/dev/task?projectId=${row.projectId || ""}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard {
  height: 100%;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money { font-size: 20px; }
      &.neg { color: #e5484d; }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

/* 卡片数不固定（含"已排期至"），且日期/任务名较长，主值行与说明行统一省略号兜底 */
.stat-grid .stat-card {
  .value {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.schedule-value { font-size: 19px; }
  }

  .sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;

    .section-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }
  }
}

.muted { color: #909399; }

.dash-body {
  padding: 6px 4px;

  .empty-block {
    color: #909399;
    padding: 12px 0;
  }

  .link-task {
    color: #409eff;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .delay-flag {
    margin-left: 6px;
    font-size: 11px;
    color: #f56c6c;
    background: #fef0f0;
    border-radius: 3px;
    padding: 0 4px;
  }

  .progress-text {
    margin-left: 6px;
    font-size: 12px;
    color: #909399;
  }

  /* 项目状态分布条：纯 CSS 绘制 */
  .dist-wrap {
    .dist-row {
      display: flex;
      align-items: center;
      margin-bottom: 6px;

      .dist-label {
        width: 60px;
        text-align: right;
        margin-right: 8px;
        font-size: 13px;
        color: #909399;
      }

      .dist-track {
        flex: 1;
        height: 14px;
        background: #f2f3f5;
        border-radius: 3px;
        overflow: hidden;

        .dist-fill {
          height: 100%;
          border-radius: 3px;
          transition: width .3s;
        }
      }

      .dist-count {
        width: 40px;
        text-align: right;
        font-weight: 600;
      }
    }
  }

  .log-list {
    .log-item {
      border-bottom: 1px dashed var(--yq-border-color, #ebeef5);
      padding: 6px 0;

      .log-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;

        .log-action {
          font-weight: 600;
        }

        .log-detail {
          color: #909399;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .log-time {
        font-size: 12px;
        color: #909399;
        margin-top: 2px;
      }
    }
  }
}
</style>
